/**
 * Helpers for sorting event agenda items chronologically.
 *
 * dates[] is YYYY-MM-DD and startTime is HH:mm, so lexicographic compare is chronological.
 * dates[] may be stored unsorted (legacy items toggled checkboxes in click order), so the
 * sort key takes the minimum entry rather than dates[0].
 */

export interface AgendaSortableItem {
  dates?: string[];
  startTime?: string;
}

/** A single agenda item paired with one of its dates. */
export interface ExpandedAgendaEntry<T extends AgendaSortableItem> {
  item: T;
  /** YYYY-MM-DD, or "" if the source item has no dates. */
  date: string;
}

/**
 * Returns the earliest date in an agenda item's dates[] array.
 * Handles unsorted dates[] (legacy items where checkbox order was preserved).
 *
 * @param item - Agenda item with optional dates array.
 * @returns Earliest YYYY-MM-DD date string, or empty string if no dates.
 */
export function earliestDate(item: AgendaSortableItem): string {
  if (!item.dates?.length) return "";
  return [...item.dates].sort()[0];
}

/**
 * String compare that sorts empty strings last (used for missing dates/times).
 *
 * @param a - First value.
 * @param b - Second value.
 * @returns Negative if a < b, positive if a > b, 0 if equal. Empty strings sort last.
 */
export function compareWithMissingLast(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

/**
 * Returns a new array of agenda items sorted by earliest date, then by start time.
 * Items missing a date or time go last.
 *
 * @param agenda - Array of agenda items to sort.
 * @returns New array sorted chronologically; original is not mutated.
 */
export function sortAgendaChronologically<T extends AgendaSortableItem>(agenda: T[]): T[] {
  return [...agenda].sort((a, b) => {
    const dateCmp = compareWithMissingLast(earliestDate(a), earliestDate(b));
    if (dateCmp !== 0) return dateCmp;
    return compareWithMissingLast(a.startTime ?? "", b.startTime ?? "");
  });
}

/**
 * Expands multi-day agenda items into one entry per date, dedupes by
 * (title + date + startTime), and sorts chronologically.
 *
 * Multi-day items are stored as a single row with dates[] holding every applicable
 * date; this helper produces the rendering-friendly shape where each date is its
 * own row. First-seen entries win the dedupe so callers can rely on input order
 * to choose which fields survive.
 *
 * @param agenda - Array of agenda items to expand.
 * @returns Sorted, deduped array of { item, date } pairs.
 */
export function expandAgendaPerDate<T extends AgendaSortableItem & { title?: string }>(
  agenda: T[]
): ExpandedAgendaEntry<T>[] {
  const expanded: ExpandedAgendaEntry<T>[] = [];
  for (const item of agenda) {
    const dates = item.dates && item.dates.length > 0 ? item.dates : [""];
    for (const date of dates) {
      expanded.push({ item, date });
    }
  }

  const seen = new Set<string>();
  const deduped = expanded.filter(({ item, date }) => {
    const key = `${item.title ?? ""}|${date}|${item.startTime ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  deduped.sort((a, b) => {
    const dateCmp = compareWithMissingLast(a.date, b.date);
    if (dateCmp !== 0) return dateCmp;
    return compareWithMissingLast(a.item.startTime ?? "", b.item.startTime ?? "");
  });

  return deduped;
}
